import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLD, { buildBreadcrumb } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { SITE_NAME, SITE_URL } from "../translations";
import { ChevronLeft } from "lucide-react";

/**
 * Shared layout for static pages (legal, about, contact).
 * Renders bilingual content from `content` prop:
 *   { en: { title, description, sections: [{h2?, body, list?}] }, fr: { ... } }
 */
export default function StaticPage({ content, slug, testid }) {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const c = content[lang] || content.en;

  useEffect(() => {
    document.title = c.title.includes(SITE_NAME) ? c.title : `${c.title} — ${SITE_NAME}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", c.description || c.title);
    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [c, location.pathname]);

  return (
    <div className="min-h-screen bg-white page-fade-in" data-testid={testid}>
      <JsonLD data={buildBreadcrumb(c.title, location.pathname)} />
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors">
            <ChevronLeft className="w-4 h-4" />{t.nav.home}
          </Link>
        </div>
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {c.title}
          </h1>
          {c.subtitle && <p className="text-slate-500 text-base">{c.subtitle}</p>}
          {c.lastUpdated && (
            <p className="text-xs text-slate-400 mt-2">
              {lang === "fr" ? "Dernière mise à jour : " : "Last updated: "}{c.lastUpdated}
            </p>
          )}
        </header>
        <article className="prose prose-slate max-w-none">
          {c.sections.map((s, i) => (
            <section key={i} className="mb-8">
              {s.h2 && <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.h2}</h2>}
              {s.body && <div className="text-slate-600 text-base leading-relaxed space-y-3">
                {s.body.split("\n\n").map((p, j) => <p key={j}>{p}</p>)}
              </div>}
              {s.list && (
                <ul className="list-disc list-inside text-slate-600 mt-3 space-y-1">
                  {s.list.map((li, j) => <li key={j}>{li}</li>)}
                </ul>
              )}
            </section>
          ))}
        </article>
      </main>
      <Footer />
    </div>
  );
}
